/**
 * Message identity helpers — one hop for every per-message event, plus the
 * single ack-time flip from `optimistic` to `persisted`.
 *
 * Every per-message event that crosses the network (MESSAGE_CREDIT_UPDATE,
 * rewind lookups, etc.) has to find the right message in the local store,
 * but the server may key by DB row id, the SDK may key by its own uuid,
 * and the frontend may still hold an optimistic `msg-<ts>-<rand>` id that
 * never left the client. Before this module, every handler had its own
 * ad-hoc findIndex that only matched one of the three and silently
 * returned on miss — that was the bug class the 2026-04-23 spec closed.
 *
 * Use `findMessageByAnyId` for any lookup that originated from a payload
 * id. Use `getMessageCanonicalId` for React keys or outbound payloads.
 */

import type { Message } from "@/stores/types";
import {
  getMessageCanonicalId as sharedCanonicalId,
  matchesAnyMessageId,
  type MessageIdentity,
} from "@bash/shared";

/**
 * Resolve an effective `MessageIdentity` for a store Message.
 *
 * Messages carrying an `identity` field go through the discriminated
 * union as-is. Messages that predate the identity field (older serialized
 * rows, legacy tests) synthesize one from their `id` / `sdkMessageUuid`:
 *  - UUID-shaped `id` → treated as a persisted `serverId`.
 *  - `msg-…` shaped `id` → treated as an optimistic `clientId`.
 * This lets every reader go through a single, safe path regardless of
 * which code path created the message.
 */
function effectiveIdentity(
  m: Pick<Message, "id" | "identity" | "sdkMessageUuid">
): MessageIdentity {
  if (m.identity) return m.identity;
  // No identity field — infer from legacy `id`.
  const looksLikeUuid =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      m.id
    );
  if (looksLikeUuid) {
    const identity: MessageIdentity = { kind: "persisted", serverId: m.id };
    if (m.sdkMessageUuid) identity.sdkMessageUuid = m.sdkMessageUuid;
    return identity;
  }
  return { kind: "optimistic", clientId: m.id };
}

/**
 * Canonical id for a store Message — safe for React keys and outbound
 * server payloads. Returns the serverId once the message is persisted,
 * otherwise the client id.
 */
export function getMessageCanonicalId(
  m: Pick<Message, "id" | "identity" | "sdkMessageUuid">
): string {
  return sharedCanonicalId(effectiveIdentity(m));
}

/**
 * Find the index of the message whose identity matches the given id —
 * client, server, or SDK uuid. Returns `-1` on miss.
 *
 * Callers handling a per-message payload don't know which flavor of id
 * the server just handed them, so we search all three in one pass. This
 * replaces dozens of `findIndex(m => m.id === x)` sites that only ever
 * matched one of the three and silently missed the rest.
 */
export function findMessageByAnyId(
  messages: ReadonlyArray<Pick<Message, "id" | "identity" | "sdkMessageUuid">>,
  id: string
): number {
  if (!id) return -1;
  for (let i = 0; i < messages.length; i++) {
    if (matchesAnyMessageId(effectiveIdentity(messages[i]), id)) return i;
  }
  return -1;
}

/**
 * Flip one optimistic message to `persisted` under the server's row id,
 * carrying BOTH join keys across.
 *
 * The ack seam of every send path runs this: the server assigned a DB row id
 * and echoed it as `ack.messageId`, so later id-keyed lookups
 * (USER_MESSAGE_BROADCAST, MESSAGE_SYNC, credit updates) must be able to match
 * by it. What the flip must NOT do is drop the keys the client still needs —
 * `clientId` keeps React keys stable and keeps `retryMessage` resending the id
 * the inbox dedups on; `wireUuid` keeps the row findable from the staging
 * namespace. Written once because `sendMessage` forks three ways and each fork
 * used to carry its own copy of this — one of which dropped `wireUuid`.
 *
 * Mutates in place: every caller runs inside an immer draft.
 */
export function markMessagePersisted(m: Message, serverId: string): void {
  const clientId =
    m.identity?.kind === "optimistic"
      ? m.identity.clientId
      : m.identity?.clientId ?? m.id;
  const wire = m.identity?.wireUuid;
  m.identity = {
    kind: "persisted",
    serverId,
    ...(clientId ? { clientId } : {}),
    ...(wire ? { wireUuid: wire } : {}),
  };
}

/**
 * Find a still-OPTIMISTIC user message whose text matches `text` — the
 * reconciliation hook for a server echo (USER_MESSAGE_BROADCAST / MESSAGE_SYNC)
 * that lands before sendMessage's ack flips the optimistic copy to persisted.
 *
 * The optimistic new-task user message carries a client `msg-…` id, while the
 * echo is keyed by the server's DB UUID — so a plain id-keyed dedup misses and
 * the echo gets appended as a duplicate. Matching on (optimistic identity +
 * role:user + text) lets the handler stamp the server id onto the existing copy
 * instead of pushing a second one. Returns `-1` on miss.
 *
 * Scoped deliberately tight: only `optimistic` user messages are candidates
 * (a `persisted` user message has already reconciled and matches by id), so a
 * genuine cross-tab message — which this tab never sent and has no optimistic
 * copy for — falls through to the append path untouched.
 */
/**
 * Find the trailing assistant message that is still OPTIMISTIC — a turn this
 * client committed from the live stream that never learned its DB row id.
 *
 * The interrupt / disconnect / reconcile AGENT_END paths carry no `messageId`,
 * so the committed turn keeps a client-only `msg-…` id that no server row can
 * match. The next MESSAGE_SYNC then appended the SAME turn a second time
 * (founder 08-20, task 54510a8b: one DB row, two bubbles). This is the anchor
 * the merge reconciles that row onto instead.
 *
 * Deliberately tight: ONLY the last message qualifies, and only while it is
 * still optimistic. A persisted assistant row has already reconciled (it
 * matches by id), and an earlier turn is never a candidate — so a genuinely new
 * turn can't be folded into an older one.
 */
export function findOptimisticAssistantTail(
  messages: ReadonlyArray<
    Pick<Message, "id" | "identity" | "sdkMessageUuid" | "role">
  >
): number {
  const last = messages.length - 1;
  if (last < 0) return -1;
  const m = messages[last];
  if (m.role !== "assistant") return -1;
  return effectiveIdentity(m).kind === "optimistic" ? last : -1;
}

export function findOptimisticUserMessage(
  messages: ReadonlyArray<
    Pick<Message, "id" | "identity" | "sdkMessageUuid" | "role" | "content">
  >,
  text: string
): number {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.role !== "user") continue;
    if (effectiveIdentity(m).kind !== "optimistic") continue;
    const msgText = m.content
      .filter(
        (b): b is Extract<typeof b, { type: "text" }> => b.type === "text"
      )
      .map((b) => b.text)
      .join("");
    if (msgText === text) return i;
  }
  return -1;
}
